import { auth } from '../firebase'; // Import Firebase auth instance

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Fetches the allowed supplier emails list with optional search and pagination.
 * List is POST (not GET) to match the house convention that reads take a JSON body.
 * @param {object} [options={}] - Optional parameters.
 * @param {number} [options.pageSize=20] - Number of results per page.
 * @param {string|null} [options.startAfterDocId=null] - Pagination cursor (doc ID).
 * @param {string} [options.search=''] - Search term.
 * @returns {Promise<{emails: Array, hasMore: boolean, lastDocId: string|null}>}
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listAllowedSupplierEmails = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const { pageSize = 20, startAfterDocId = null, search = '' } = options;

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/allowed-supplier-emails/list`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ pageSize, startAfterDocId, search }),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    // data/hasMore/lastDocId are siblings on the response, not nested under data.
    return {
      emails: responseData.data || [],
      hasMore: !!responseData.hasMore,
      lastDocId: responseData.lastDocId || null,
    };

  } catch (error) {
    console.error("Error listing allowed supplier emails:", error);
    throw error;
  }
};

/**
 * Adds an email to the supplier allowlist, bound to a specific Supplier (Firestore 'clients').
 * The admin chooses the Supplier here — the signer no longer picks their own during signup.
 * @param {string} email - The email address to add.
 * @param {string} clientId - The Supplier this email is allowed to access.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const addAllowedSupplierEmail = async (email, clientId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!email) {
    throw new Error("Email is required.");
  }
  if (!clientId) {
    throw new Error("Supplier is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/allowed-supplier-emails/add`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, clientId }),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;

  } catch (error) {
    console.error("Error adding allowed supplier email:", error);
    throw error;
  }
};

/**
 * Updates an existing supplier allowlist entry: rename the email (only while status is
 * 'new' - the doc id IS the email) and/or reassign the Supplier. Provide newEmail and/or
 * clientId; at least one is required.
 * @param {object} params
 * @param {string} params.email - The existing email identifying the entry (Firestore doc id).
 * @param {string} [params.newEmail] - New email to rename the entry to.
 * @param {string} [params.clientId] - New Supplier (Firestore 'clients') to reassign to.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const updateAllowedSupplierEmail = async ({ email, newEmail, clientId } = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!email) {
    throw new Error("Email is required.");
  }
  if (!newEmail && !clientId) {
    throw new Error("Provide a new email or a Supplier to update.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/allowed-supplier-emails/update`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, newEmail, clientId }),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;

  } catch (error) {
    console.error("Error updating allowed supplier email:", error);
    throw error;
  }
};

/**
 * Removes an email from the supplier allowlist.
 * @param {string} email - The email address to remove.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const removeAllowedSupplierEmail = async (email) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!email) {
    throw new Error("Email is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/allowed-supplier-emails/remove`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;

  } catch (error) {
    console.error("Error removing allowed supplier email:", error);
    throw error;
  }
};
